import React, { useEffect, useMemo, useState } from 'react';

import {
  type Combinations,
  type FeatureType,
  type ProductdiversityType,
} from '@/common/types/product-features.type';

import ButtonSelect from '@/common/components/button-select/button-select.component';
import Text from '@/common/components/text/text.component';
import VarientSkeleton from '@/common/components/varient-skeleton';
import { FeatureNameConstant } from '@/common/constants/feature-name.constant';

interface Props {
  combination: Combinations[];
  features: string[];
  diversity: ProductdiversityType;
  setter: (foundObject: Combinations | undefined, features: string[]) => void;
}

const getVariantValues = (selected: Combinations, features: string[]) =>
  features.reduce<Record<string, string | null>>((values, feature) => {
    values[feature] = selected[feature] ?? null;
    return values;
  }, {});

function FeaturesPreview({ diversity, features, combination, setter }: Props) {
  const [variant, setVariant] = useState<Record<string, string | null>>({});

  const selectedCombination = useMemo(
    () =>
      combination.find(
        (item) =>
          item.inStock &&
          features.every((feature) => item[feature] === variant[feature])
      ),
    [combination, features, variant]
  );

  useEffect(() => {
    if (!combination.length || !features.length || selectedCombination) return;

    const firstAvailableCombination = combination.find((item) => item.inStock);
    if (firstAvailableCombination) {
      setVariant(getVariantValues(firstAvailableCombination, features));
    }
  }, [combination, features, selectedCombination]);

  useEffect(() => {
    setter(selectedCombination, features);
  }, [selectedCombination, features, setter]);

  const selectionHandler = (value: string, secondFeature: boolean) => {
    const feature = secondFeature ? features[1] : features[0];
    const selected = combination.find(
      (item) =>
        item.inStock &&
        item[feature] === value &&
        (secondFeature ? item[features[0]] === variant[features[0]] : true)
    );

    if (selected) setVariant(getVariantValues(selected, features));
  };

  const isAvailable = (feature: string, value: string, secondFeature: boolean) =>
    combination.some(
      (item) =>
        item.inStock &&
        item[feature] === value &&
        (!secondFeature || item[features[0]] === variant[features[0]])
    );

  return (
    <>
      {features.length > 0 && combination.length === 0 ? (
        <VarientSkeleton featuresCount={features.length} />
      ) : (
        <section>
          <div className="pointer-events-none absolute inset-0 z-50 shadow-[inset_20px_0px_30px_rgba(255,255,255,0.7)]" />
          <ul className="">
            {features.map((feature: string, index: number) => {
              return (
                <li
                  key={feature}
                  className="relative z-10 my-3 w-full overflow-scroll text-right"
                >
                  <Text className="text-xs text-black-400 lg:!mb-[8px] lg:!text-sm lg:!font-medium">
                    {FeatureNameConstant[feature]}
                  </Text>
                  <ButtonSelect
                    className="my-1 mb-4 gap-3 overflow-scroll whitespace-nowrap"
                    defaultChecked={variant[features[0]] ?? undefined}
                    secondFeature={index === 1}
                    setSelected={selectionHandler}
                    variant={variant}
                    features={features}
                  >
                    {diversity[feature].map((item: FeatureType) => {
                      const optionIsAvailable = isAvailable(
                        feature,
                        item.value,
                        index === 1
                      );

                      return (
                        <ButtonSelect.Item
                          value={item.value}
                          key={item.value}
                          className="mx-3"
                          ButtonClassName="px-1 truncate min-w-[72px] w-auto mx-[6px] last:ml-0 first:mr-0 !inline-block"
                          notInStock={index === 1 && !optionIsAvailable}
                          notActive={index !== 1 && !optionIsAvailable}
                        >
                          {item.value}
                        </ButtonSelect.Item>
                      );
                    })}
                  </ButtonSelect>
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </>
  );
}

export default FeaturesPreview;
